import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Folder, 
  FolderOpen, 
  FileText, 
  ChevronRight, 
  ChevronDown, 
  Calendar, 
  Download, 
  Eye,
  MessageSquare
} from 'lucide-react';

const FolderTree = ({ tree, onFolderSelect, selectedFolder, showComments = false, onNavigate }) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTreeItem = (item, depth = 0) => {
    const isExpanded = expandedFolders.has(item.id);
    const isSelected = selectedFolder === item.path;
    const paddingLeft = depth * 20;

    if (item.type === 'folder') {
      return (
        <div key={item.id} className="folder-item">
          <div 
            className={`flex items-center space-x-2 p-3 hover:bg-gray-50 cursor-pointer rounded-lg transition-colors ${
              isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
            }`}
            style={{ paddingLeft: paddingLeft + 12 }}
            onClick={() => {
              toggleFolder(item.id);
              if (onFolderSelect) {
                onFolderSelect(item);
              }
            }}
            onDoubleClick={() => {
              if (onNavigate) {
                onNavigate(item.path);
              }
            }}
          >
            {/* Hierarchical indicator lines */}
            {depth > 0 && (
              <div className="absolute" style={{ left: paddingLeft - 8 }}>
                <div className="w-0.5 h-full bg-gray-300"></div>
              </div>
            )}
            
            <div className="flex items-center space-x-2 flex-1">
              {item.children && item.children.length > 0 && (
                <button className="p-1 hover:bg-gray-200 rounded">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-600" />
                  )}
                </button>
              )}
              
              {isExpanded ? (
                <FolderOpen className="h-5 w-5 text-blue-600" />
              ) : (
                <Folder className="h-5 w-5 text-blue-600" />
              )}
              
              <div className="flex-1">
                <span className="font-medium text-gray-900">{item.name}</span>
                {depth > 0 && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    📁 {item.path.replace(/\//g, ' → ')}
                  </div>
                )}
              </div>
              
              {showComments && (
                <MessageSquare className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
              )}
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(item.modified)}</span>
            </div>
          </div>
          
          {isExpanded && item.children && item.children.length > 0 && (
            <div className="relative">
              {/* Vertical line for children */}
              {depth >= 0 && (
                <div className="absolute" style={{ left: paddingLeft + 20 }}>
                  <div className="w-0.5 bg-gray-200" style={{ height: item.children.length * 60 }}></div>
                </div>
              )}
              <div className="ml-4">
                {item.children.map(child => renderTreeItem(child, depth + 1))}
              </div>
            </div>
          )}
        </div>
      );
    } else {
      // File item
      return (
        <div 
          key={item.id} 
          className="file-item flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors relative"
          style={{ paddingLeft: paddingLeft + 12 }}
        >
          {/* Hierarchical indicator lines for files */}
          {depth > 0 && (
            <div className="absolute" style={{ left: paddingLeft - 8 }}>
              <div className="w-0.5 h-full bg-gray-300"></div>
            </div>
          )}
          
          <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate" title={item.name}>
              {item.name}
            </h3>
            {depth > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                📁 {item.path.replace('/' + item.name, '').replace(/\//g, ' → ')}
              </p>
            )}
            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <Download className="h-3 w-3" />
                <span>{formatFileSize(item.size)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="h-3 w-3" />
                <span>{formatDate(item.modified)}</span>
              </div>
            </div>
          </div>
          
          <Link
            to={`/pdf/${item.id}`}
            className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Eye className="h-4 w-4" />
            <span>Megtekintés</span>
          </Link>
        </div>
      );
    }
  };

  if (!tree || tree.length === 0) {
    return (
      <div className="text-center py-12">
        <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Nincsenek fájlok</h3>
        <p className="text-gray-600">Jelenleg nincsenek PDF fájlok vagy mappák.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {tree.map(item => renderTreeItem(item))}
    </div>
  );
};

export default FolderTree; 